import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CaretDown, CaretRight } from "@phosphor-icons/react";
import {
  AgeChip,
  BackLink,
  CardWrapper,
  Collapsible,
  CollapsibleCount,
  CollapsibleHeader,
  PiAside,
  PiCallBlock,
  PiFine,
  PiFlags,
  PiHead,
  PiLayout,
  PiLight,
  PiLightNote,
  PiLights,
  PiMain,
  PiMeta,
  PiName,
  PiNotes,
  PiRowValue,
  PiSummary,
  QuestionBlock,
  RowLabel,
  RowList,
  Section,
  SectionTitle,
} from "../IntakeReviewStyled";
import {
  closeIntakeReviewCard,
  noteSectionOpened,
} from "../../../slice/IntakeReviewSlice";
import { ageLabel } from "../utils";
import PiDecisionBar from "./PiDecisionBar";

/**
 * One group of facts. Liability opens by default — it is what the
 * reviewer reads first — and the rest open on a tap, which is recorded
 * so the firm learns which facts the decision actually rests on.
 */
const PiSection = ({ section, defaultOpen = false }) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(defaultOpen);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) dispatch(noteSectionOpened(section.key));
  };

  return (
    <Collapsible>
      <CollapsibleHeader type="button" onClick={toggle} aria-expanded={open}>
        <span>
          {open ? <CaretDown size={14} /> : <CaretRight size={14} />} {section.title}
        </span>
        <CollapsibleCount>
          {section.row_count} {section.row_count === 1 ? "fact" : "facts"}
        </CollapsibleCount>
      </CollapsibleHeader>
      {open ? (
        <RowList>
          {(section.rows || []).map((row, index) => (
            <div key={`${section.key}-${index}`} style={{ display: "contents" }}>
              <RowLabel>{row.label}</RowLabel>
              <PiRowValue $missing={Boolean(row.missing)}>{row.value}</PiRowValue>
            </div>
          ))}
        </RowList>
      ) : null}
    </Collapsible>
  );
};

/** The agent's notes exactly as typed, behind a tap. */
const NotesSection = ({ notes }) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) dispatch(noteSectionOpened("notes"));
  };

  return (
    <Collapsible>
      <CollapsibleHeader type="button" onClick={toggle} aria-expanded={open}>
        <span>
          {open ? <CaretDown size={14} /> : <CaretRight size={14} />} Intake agent&apos;s
          notes, as typed
        </span>
        <CollapsibleCount>{notes.length > 900 ? "long" : "short"}</CollapsibleCount>
      </CollapsibleHeader>
      {open ? <PiNotes style={{ padding: "8px 12px" }}>{notes}</PiNotes> : null}
    </Collapsible>
  );
};

/**
 * The personal-injury card, as it reads on a phone.
 *
 * Name, where, when; the one line WHG Docs wrote from the intake agent's
 * notes; the firm's five minimum criteria as lights (the criteria are
 * unchanged — Matthew, 2026-10-31); what to read before deciding; then
 * the facts sorted under the questions an attorney asks — liability,
 * coverage and collectability, injuries and treatment, client — with
 * what the script never asked shown in amber rather than dropped. The
 * agent's notes sit at the bottom exactly as typed.
 */
const PiReviewCard = () => {
  const dispatch = useDispatch();
  const { card, cardLoading, cardError } = useSelector(
    (state) => state.intakeReviews
  );

  if (cardLoading) return null;
  if (cardError) return null;
  if (!card) return null;

  const snapshot = card.snapshot || {};
  const identity = snapshot.identity || {};
  const where = snapshot.where || {};
  const lights = Array.isArray(snapshot.lights) ? snapshot.lights : [];
  const flags = Array.isArray(snapshot.flags) ? snapshot.flags : [];
  const sections = Array.isArray(snapshot.sections) ? snapshot.sections : [];
  const priorQuestion = snapshot.prior_question || {};
  const callRequest = card.callRequest || {};
  const notedLights = lights.filter((l) => l.state !== "pass" && l.detail);

  return (
    <CardWrapper>
      <BackLink type="button" onClick={() => dispatch(closeIntakeReviewCard())}>
        ← Back to the queue
      </BackLink>

      <PiLayout>
        <PiMain>
          <PiHead>
            <PiName>{identity.person || card.clientName || "Unnamed intake"}</PiName>
            <PiMeta>
              {identity.case_type_short || identity.case_type || "Personal injury"}
              {where.short ? ` · ${where.short}` : ""}
              {identity.age ? ` · age ${identity.age}` : ""}
            </PiMeta>
            {snapshot.age_line ? <PiFine>{snapshot.age_line}</PiFine> : null}
            {snapshot.deadline_line ? <PiFine>{snapshot.deadline_line}</PiFine> : null}
            <PiFine>
              {typeof card.ageDays === "number" ? (
                <AgeChip $tone={card.ageTone} title={card.waitingSince || ""}>
                  Waiting {ageLabel(card.ageDays)}
                </AgeChip>
              ) : null}
              {card.intakeNumber ? <span> · {card.intakeNumber}</span> : null}
              {snapshot.source?.name ? <span> · via {snapshot.source.name}</span> : null}
            </PiFine>
          </PiHead>

          {snapshot.summary ? <PiSummary>{snapshot.summary}</PiSummary> : null}

          {lights.length ? (
            <>
              <PiLights>
                {lights.map((light) => (
                  <PiLight key={light.key} $state={light.state} title={light.detail || ""}>
                    {light.label}
                  </PiLight>
                ))}
              </PiLights>
              {notedLights.map((light) => (
                <PiLightNote key={`${light.key}-note`}>
                  {light.label}: {light.detail}
                </PiLightNote>
              ))}
            </>
          ) : null}

          {flags.length ? (
            <PiFlags>
              <strong>Before you decide</strong>
              {flags.map((flag) => (
                <div key={flag}>{flag}</div>
              ))}
            </PiFlags>
          ) : null}

          {callRequest.to ? (
            <PiCallBlock>
              <strong>
                {callRequest.answer
                  ? `${callRequest.answered_by_name || "Your colleague"} called the client`
                  : `${callRequest.asked_by_name || "The reviewer"} asked ${
                      callRequest.to_name || callRequest.to
                    } to call the client`}
              </strong>
              {callRequest.note ? <div>Asked: {callRequest.note}</div> : null}
              {callRequest.answer ? <div>They said: {callRequest.answer}</div> : null}
            </PiCallBlock>
          ) : null}

          {priorQuestion.question ? (
            <Section>
              <QuestionBlock>
                <strong>This went back to intake with a question</strong>
                {priorQuestion.question}
                {priorQuestion.answer ? (
                  <>
                    <strong style={{ marginTop: 8 }}>Intake answered</strong>
                    {priorQuestion.answer}
                  </>
                ) : (
                  <em> — no answer yet.</em>
                )}
              </QuestionBlock>
            </Section>
          ) : null}

          {sections.length ? (
            <Section>
              {sections.map((section) => (
                <PiSection
                  key={section.key}
                  section={section}
                  defaultOpen={section.key === "liability"}
                />
              ))}
            </Section>
          ) : null}

          {snapshot.notes ? (
            <Section>
              <NotesSection notes={snapshot.notes} />
            </Section>
          ) : null}

          {snapshot.intake_summary ? (
            <Section>
              <SectionTitle>Intake&apos;s own summary</SectionTitle>
              <PiNotes>{snapshot.intake_summary}</PiNotes>
            </Section>
          ) : null}
        </PiMain>

        <PiAside>
          <PiDecisionBar />
        </PiAside>
      </PiLayout>
    </CardWrapper>
  );
};

export default PiReviewCard;
